import Vue from 'vue';
import Router from 'vue-router';
import HomePage from '../views/HomePage.vue';
import ProductLight530 from '../views/product/ProductLight530.vue';
import ProductLight640 from '../views/product/ProductLight640.vue';
import ProductLightbule530 from '../views/product/ProductLightbule530.vue';
import ProductLightm20 from '../views/product/ProductLightm20.vue';
import ProductKits from '../views/product/ProductKits.vue';
import ProductCourses from '../views/product/ProductCourses.vue';
import AccessoriesPage from '../views/product/AccessoriesPage.vue';
import KitsAccessories from '../views/product/KitsAccessories.vue';
import SoftwarePage from '../views/product/SoftwarePage.vue';
import LearningPlatform from '../views/software/LearningPlatform.vue';
import CloudPlatform from '../views/software/CloudPlatform.vue';
import LaserSoftware from '../views/software/LaserSoftware.vue';
import EduServicesPage from '../views/edu/EduServicesPage.vue';
import EduCooperation from '../views/edu/EduCooperation.vue';
import MakerK12 from '../views/edu/MakerK12.vue';
import ArchitecturalVocational from '../views/edu/ArchitecturalVocational.vue';
import CulturalHigherVocational from '../views/edu/CulturalHigherVocational.vue';
import ArtsUniversity from '../views/edu/ArtsUniversity.vue';
import AfterSalesService from '../views/support/AfterSalesService.vue';
import DownloadCenter from '../views/support/DownloadCenter.vue';
import AboutPage from '../views/AboutPage.vue';
import NewsInformation from '../views/about/NewsInformation.vue';

Vue.use(Router);

function setupPageStayReporter(router) {
  const endpoints = ['/api/ip/save', '/ip/save'];
  const minSeconds = 1;

  let currentPath = null;
  let accumulatedMs = 0;
  let running = false;
  let lastStartAt = 0;
  let unloadFlushed = false;

  const now = () => Date.now();

  const pause = () => {
    if (!currentPath) return;
    if (!running) return;
    accumulatedMs += Math.max(0, now() - lastStartAt);
    running = false;
    lastStartAt = 0;
  };

  const resume = () => {
    if (!currentPath) return;
    if (running) return;
    running = true;
    lastStartAt = now();
  };

  const totalMs = () => {
    if (!currentPath) return 0;
    return accumulatedMs + (running ? Math.max(0, now() - lastStartAt) : 0);
  };

  const send = (payload, preferBeacon) => {
    const body = JSON.stringify(payload);

    const postJson = (url) =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        keepalive: !!preferBeacon,
      });

    if (
      preferBeacon &&
      navigator &&
      typeof navigator.sendBeacon === 'function'
    ) {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        for (const url of endpoints) {
          const ok = navigator.sendBeacon(url, blob);
          if (ok) return;
        }
      } catch (e) {
        void e;
      }
    }

    const [primary, fallback] = endpoints;
    postJson(primary)
      .then((res) => {
        if (!fallback) return;
        if (res && res.status === 404) {
          return postJson(fallback);
        }
      })
      .catch(() => {
        if (!fallback) return;
        return postJson(fallback).catch(() => undefined);
      });
  };

  const flush = (preferBeacon) => {
    if (!currentPath) return;
    const seconds = Math.max(minSeconds, Math.floor(totalMs() / 1000));
    send({ visitPage: currentPath, stopTime: seconds }, preferBeacon);
  };

  const start = (path) => {
    currentPath = path || '/';
    accumulatedMs = 0;
    unloadFlushed = false;
    if (document && document.hidden) {
      running = false;
      lastStartAt = 0;
    } else {
      running = true;
      lastStartAt = now();
    }
  };

  router.beforeEach((to, from, next) => {
    if (currentPath) {
      flush(false);
      pause();
    }
    next();
  });

  router.afterEach((to) => {
    start(to && to.path);
  });

  const onVisibilityChange = () => {
    if (!currentPath) return;
    if (document.hidden) {
      pause();
      return;
    }
    resume();
  };

  const onPageExit = () => {
    if (unloadFlushed) return;
    unloadFlushed = true;
    pause();
    flush(true);
  };

  if (document && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', onVisibilityChange, true);
  }

  if (window && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', onPageExit, true);
    window.addEventListener('beforeunload', onPageExit, true);
  }
}

const router = new Router({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes: [
    { path: '/', name: 'home', component: HomePage },
    {
      path: '/product/light530',
      name: 'product-light530',
      component: ProductLight530,
    },
    {
      path: '/product/light640',
      name: 'product-light640',
      component: ProductLight640,
    },
    {
      path: '/product/blue530',
      name: 'product-blue530',
      component: ProductLightbule530,
    },
    {
      path: '/product/m20',
      name: 'product-m20',
      component: ProductLightm20,
    },
    {
      path: '/product/kits',
      name: 'product-kits',
      component: ProductKits,
    },
    {
      path: '/product/courses',
      name: 'product-courses',
      component: ProductCourses,
    },
    {
      path: '/product/accessories',
      name: 'accessories',
      component: AccessoriesPage,
    },
    {
      path: '/product/kits-accessories',
      name: 'kits-accessories',
      component: KitsAccessories,
    },
    {
      path: '/product/software',
      name: 'software',
      component: SoftwarePage,
    },
    {
      path: '/software/learning-platform',
      name: 'learning-platform',
      component: LearningPlatform,
    },
    {
      path: '/software/cloud-platform',
      name: 'cloud-platform',
      component: CloudPlatform,
    },
    {
      path: '/software/laser-software',
      name: 'laser-software',
      component: LaserSoftware,
    },
    {
      path: '/edu-services',
      name: 'edu-services',
      component: EduServicesPage,
    },
    {
      path: '/edu-services/cooperation',
      name: 'edu-cooperation',
      component: EduCooperation,
    },
    {
      path: '/edu-services/maker-k12',
      name: 'maker-k12',
      component: MakerK12,
    },
    {
      path: '/edu-services/architectural-vocational',
      name: 'architectural-vocational',
      component: ArchitecturalVocational,
    },
    {
      path: '/edu-services/cultural-higher-vocational',
      name: 'cultural-higher-vocational',
      component: CulturalHigherVocational,
    },
    {
      path: '/edu-services/arts-university',
      name: 'arts-university',
      component: ArtsUniversity,
    },
    {
      path: '/support/after-sales',
      name: 'after-sales',
      component: AfterSalesService,
    },
    {
      path: '/support/download',
      name: 'download-center',
      component: DownloadCenter,
    },
    { path: '/about', name: 'about', component: AboutPage },
    { path: '/about/news', name: 'news', component: NewsInformation },
    { path: '/about/news/:id', name: 'news-detail', component: NewsInformation },
    { path: '*', redirect: '/' },
  ],
});

setupPageStayReporter(router);

export default router;
